import { Component, input, output, inject, Inject, Optional, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DialogActionsComponent, DialogAction } from '../dialog-actions/dialog-actions.component';
import { FeatureFlagsService } from '@kanek/shared-data-access';

export interface ModalConfig {
  // Header configuration
  title: string;
  subtitle?: string;
  icon?: string;
  iconColor?: string;

  // Size and appearance
  width?: string;
  maxWidth?: string;
  minWidth?: string;
  height?: string;
  maxHeight?: string;

  // Behavior
  closable?: boolean;
  closeOnBackdropClick?: boolean;
  preventClose?: boolean;

  // Loading state
  loading?: boolean;
  loadingText?: string;

  // Actions
  actions?: DialogAction[];

  // Variants
  variant?: 'default' | 'confirmation' | 'form' | 'fullscreen';

  // Styling
  dangerous?: boolean;
  noPadding?: boolean;
}

/**
 * SharedModalComponent - A comprehensive modal component for all dialog needs
 *
 * Supports multiple modal patterns:
 * - default: Standard dialog with header, content, actions
 * - confirmation: Confirmation dialog with emphasis on actions
 * - form: Form dialog with proper spacing and validation
 * - fullscreen: Full-screen dialog for complex workflows
 *
 * Features:
 * - Flexible content projection (template or component)
 * - Consistent action button patterns via DialogActionsComponent
 * - Loading states with spinner and disabled actions
 * - Responsive sizing options
 * - Design token integration
 * - Feature flag controlled rollout
 * - Accessibility compliant
 * - Dangerous action styling
 */
@Component({
  selector: 'lib-shared-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DialogActionsComponent
  ],
  template: `
    <div class="modal-container"
         [class.loading]="config().loading"
         [class.dangerous]="config().dangerous"
         [class.no-padding]="config().noPadding">

      <!-- Header -->
      <div class="modal-header"
           [class.confirmation]="config().variant === 'confirmation'">
        <div class="header-content">
          @if (config().icon) {
            <mat-icon class="header-icon"
                     [class.dangerous]="config().dangerous"
                     [style.color]="config().iconColor || 'var(--sys-primary)'">
              {{ config().icon }}
            </mat-icon>
          }

          <div class="header-text">
            <h2 mat-dialog-title class="modal-title">{{ config().title }}</h2>
            @if (config().subtitle) {
              <p class="modal-subtitle">{{ config().subtitle }}</p>
            }
          </div>
        </div>

        @if (config().closable !== false) {
          <button mat-icon-button
                  class="close-button"
                  [disabled]="config().preventClose || config().loading"
                  (click)="onClose()"
                  aria-label="Close dialog">
            <mat-icon>close</mat-icon>
          </button>
        }
      </div>

      <!-- Loading overlay -->
      @if (config().loading) {
        <div class="modal-loading-overlay">
          <mat-spinner diameter="32"></mat-spinner>
          @if (config().loadingText) {
            <p class="loading-text">{{ config().loadingText }}</p>
          }
        </div>
      }

      <!-- Content -->
      <mat-dialog-content class="modal-content"
                          [class.form-content]="config().variant === 'form'"
                          [class.confirmation-content]="config().variant === 'confirmation'"
                          [class.fullscreen-content]="config().variant === 'fullscreen'">
        <ng-content></ng-content>
      </mat-dialog-content>

      <!-- Actions -->
      @if (config().actions?.length) {
        <lib-dialog-actions
          [actions]="getProcessedActions()"
          (actionClick)="onAction($event)">
        </lib-dialog-actions>
      }
    </div>
  `,
  styles: [`
    .modal-container {
      position: relative;
      display: flex;
      flex-direction: column;
      max-height: 90vh;
      min-width: 320px;
      background: var(--sys-surface-container-high);
      color: var(--sys-on-surface);
      border-radius: var(--border-radius-lg);
      overflow: hidden;

      &.loading {
        pointer-events: none;

        .modal-content {
          opacity: 0.6;
        }
      }

      &.dangerous {
        .header-icon {
          color: var(--sys-error) !important;
        }

        .modal-title {
          color: var(--sys-error);
        }
      }

      &.no-padding .modal-content {
        padding: 0;
      }
    }

    .modal-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      padding: var(--spacing-lg) var(--spacing-lg) var(--spacing-md) var(--spacing-lg);
      border-bottom: 1px solid var(--sys-outline-variant);
      flex-shrink: 0;

      &.confirmation {
        text-align: center;
        flex-direction: column;
        gap: var(--spacing-sm);

        .header-content {
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .close-button {
          position: absolute;
          top: var(--spacing-sm);
          right: var(--spacing-sm);
        }
      }
    }

    .header-content {
      display: flex;
      align-items: center;
      gap: var(--spacing-md);
      flex: 1;
      min-width: 0; // Prevent flex item overflow
    }

    .header-icon {
      font-size: 2rem;
      width: 2rem;
      height: 2rem;
      flex-shrink: 0;

      &.dangerous {
        animation: pulse-danger 2s infinite;
      }
    }

    .header-text {
      flex: 1;
      min-width: 0;
    }

    .modal-title {
      margin: 0 0 var(--spacing-xs) 0;
      font-family: var(--font-family-display);
      font-size: var(--typography-title-large-size);
      font-weight: var(--typography-title-large-weight);
      letter-spacing: 0.05em;
      line-height: var(--line-height-tight);
      color: var(--sys-on-surface);

      // Truncate long titles
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .modal-subtitle {
      margin: 0;
      font-size: var(--typography-body-medium-size);
      font-weight: var(--typography-body-medium-weight);
      color: var(--sys-on-surface-variant);
      line-height: var(--line-height-normal);
    }

    .close-button {
      flex-shrink: 0;
      color: var(--sys-on-surface-variant);

      &:hover {
        background-color: var(--sys-surface-variant);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .modal-loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(var(--sys-surface-rgb), 0.8);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: var(--spacing-md);
      z-index: 10;

      .loading-text {
        margin: 0;
        font-size: var(--typography-body-medium-size);
        color: var(--sys-on-surface-variant);
      }
    }

    .modal-content {
      flex: 1;
      overflow-y: auto;
      padding: var(--spacing-lg);

      &.form-content {
        padding: var(--spacing-xl) var(--spacing-lg);

        // Enhanced form spacing
        ::ng-deep {
          form {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-lg);

            mat-form-field {
              width: 100%;
            }
          }
        }
      }

      &.confirmation-content {
        text-align: center;
        padding: var(--spacing-lg) var(--spacing-xl);

        p {
          font-size: var(--typography-body-large-size);
          line-height: var(--line-height-relaxed);
          margin: 0;
          color: var(--sys-on-surface-variant);
        }
      }

      &.fullscreen-content {
        padding: var(--spacing-xl);
      }
    }

    @keyframes pulse-danger {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.7;
      }
    }

    // Responsive design
    @media (max-width: 600px) {
      .modal-container {
        margin: var(--spacing-sm);
        max-height: calc(100vh - var(--spacing-lg));
        min-width: auto;
        width: calc(100vw - var(--spacing-lg));
      }

      .modal-header {
        padding: var(--spacing-md);

        .header-content {
          gap: var(--spacing-sm);
        }

        .header-icon {
          font-size: 1.5rem;
          width: 1.5rem;
          height: 1.5rem;
        }

        .modal-title {
          font-size: var(--typography-title-medium-size);
        }
      }

      .modal-content {
        padding: var(--spacing-md);

        &.form-content {
          padding: var(--spacing-lg) var(--spacing-md);
        }

        &.confirmation-content {
          padding: var(--spacing-lg) var(--spacing-md);
        }
      }
    }
  `]
})
export class SharedModalComponent {
  // Input signal for template binding usage
  protected configInput = input<ModalConfig>({ title: '' });
  actionEmitted = output<string>();
  modalClosed = output<void>();

  private featureFlags = inject(FeatureFlagsService);
  private dialogRef = inject(MatDialogRef<SharedModalComponent>, { optional: true });
  private dialogData = inject(MAT_DIALOG_DATA, { optional: true });

  // This computed signal provides the final config
  config = computed(() => {
    // Priority: 1) Dialog data, 2) Input signal
    if (this.dialogData?.config) {
      return this.dialogData.config;
    }
    return this.configInput();
  });

  constructor() {
    // Feature flag check
    if (!this.featureFlags.useSharedModalComponent()) {
      console.warn('SharedModalComponent: Feature flag "useSharedModalComponent" is disabled');
    }
  }

  /**
   * Process actions to apply loading state and disabled conditions
   */
  getProcessedActions(): DialogAction[] {
    if (!this.config().actions) return [];

    return this.config().actions.map((action: DialogAction) => ({
      ...action,
      disabled: this.config().loading ||
                (typeof action.disabled === 'function' ? action.disabled() : action.disabled)
    }));
  }

  /**
   * Handle action clicks
   */
  onAction(actionType: string): void {
    // Don't process actions if loading
    if (this.config().loading) {
      return;
    }

    this.actionEmitted.emit(actionType);

    // Auto-close on certain actions unless preventClose is set
    if (!this.config().preventClose && (actionType === 'cancel' || actionType === 'close')) {
      this.onClose();
    }
  }

  /**
   * Handle close button click
   */
  onClose(): void {
    if (this.config().preventClose || this.config().loading) {
      return;
    }

    this.modalClosed.emit();
    this.dialogRef?.close();
  }

  /**
   * Helper method to create common confirmation dialog configurations
   */
  static createConfirmationConfig(
    title: string,
    message: string,
    options: Partial<ModalConfig> = {}
  ): ModalConfig {
    return {
      title,
      subtitle: message,
      variant: 'confirmation',
      icon: 'help_outline',
      width: '400px',
      actions: [
        {
          label: 'Cancel',
          action: 'cancel',
          variant: 'flat'
        },
        {
          label: 'Confirm',
          action: 'confirm',
          variant: 'raised',
          color: 'primary'
        }
      ],
      ...options
    };
  }

  /**
   * Helper method to create dangerous confirmation dialog configurations
   */
  static createDangerousConfirmationConfig(
    title: string,
    message: string,
    confirmText = 'Delete',
    options: Partial<ModalConfig> = {}
  ): ModalConfig {
    return {
      title,
      subtitle: message,
      variant: 'confirmation',
      icon: 'warning',
      dangerous: true,
      width: '400px',
      actions: [
        {
          label: 'Cancel',
          action: 'cancel',
          variant: 'flat'
        },
        {
          label: confirmText,
          action: 'confirm',
          variant: 'raised',
          color: 'warn'
        }
      ],
      ...options
    };
  }

  /**
   * Helper method to create form dialog configurations
   */
  static createFormConfig(
    title: string,
    subtitle?: string,
    options: Partial<ModalConfig> = {}
  ): ModalConfig {
    return {
      title,
      subtitle,
      variant: 'form',
      icon: 'edit',
      width: '600px',
      maxWidth: '90vw',
      actions: [
        {
          label: 'Cancel',
          action: 'cancel',
          variant: 'flat'
        },
        {
          label: 'Save',
          action: 'save',
          variant: 'raised',
          color: 'primary'
        }
      ],
      ...options
    };
  }

  /**
   * Helper method to create fullscreen dialog configurations
   */
  static createFullscreenConfig(
    title: string,
    subtitle?: string,
    options: Partial<ModalConfig> = {}
  ): ModalConfig {
    return {
      title,
      subtitle,
      variant: 'fullscreen',
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      closable: true,
      ...options
    };
  }
}